import {useCallback, useEffect} from 'react';

import {GroupsRequester} from '../../Domain/Groups/GroupsRequester';
import {
    hydrateOriginalFromBulkEntries,
    processAccessEntries,
} from './accessNodeHelpers';

const useAccessFetch = ({
    workspace, targetIds, inheritedIds, isBulkMode,
    accessMap, setAccessMap, inheritedSources,
    original, setOriginal, setMixed, edits, tree,
}) => {
    const applyAccessEntries = useCallback((entries) => {
        const newAccessMap = processAccessEntries(entries, targetIds, inheritedSources, isBulkMode);
        setAccessMap((prev) => ({...prev, ...newAccessMap}));

        const hydrated = hydrateOriginalFromBulkEntries(entries, targetIds);
        setOriginal((prev) => ({
            includeAll: new Set([...prev.includeAll, ...hydrated.includeAll]),
            notIncludeAll: new Set([...prev.notIncludeAll, ...hydrated.notIncludeAll]),
        }));
        if (isBulkMode) {
            setMixed((prev) => {
                const next = new Map(prev);
                for (const [id, mixedInfo] of hydrated.mixed) next.set(id, mixedInfo);
                return next;
            });
        }
    }, [targetIds, inheritedSources, isBulkMode, setAccessMap, setOriginal, setMixed]);

    const isNodeCovered = useCallback((nodeId) => {
        const info = accessMap[nodeId];
        const directCovered = info?.isDirectIncludeAll || original.includeAll.has(nodeId) || edits.addedIncludeAll.has(nodeId);
        const inheritedCovered = info?.inheritedIncludeAll;
        return {directCovered, inheritedCovered};
    }, [accessMap, original, edits]);

    const hasTargets = targetIds.length > 0;
    const hasInherited = inheritedIds.length > 0;

    const fetchAccessForNodes = useCallback(async (nodeId, nestedRoute, isProjectLevel = false) => {
        if ((!hasTargets && !hasInherited) || !workspace) return;

        if (!isProjectLevel) {
            const {directCovered, inheritedCovered} = isNodeCovered(nodeId);
            if (directCovered && inheritedCovered) return;
        }

        const {directCovered} = isNodeCovered(nodeId);
        const needsDirect = hasTargets && !directCovered;
        const needsInherited = !accessMap[nodeId]?.inheritedIncludeAll;

        try {
            const result = await GroupsRequester.getAccessForPage(workspace, {
                targetIds: needsDirect ? targetIds : null,
                inheritedIds: needsInherited ? inheritedIds : null,
                nestedRoute,
                isProjectLevel,
            });
            applyAccessEntries(result.entries);
        } catch (error) {
            console.error('Failed to fetch access for page:', error);
        }
    }, [hasTargets, hasInherited, workspace, targetIds, inheritedIds, accessMap, isNodeCovered, applyAccessEntries]);

    useEffect(() => {
        if (tree.projects.length > 0 && (hasTargets || hasInherited)) {
            fetchAccessForNodes(null, '', true);
        }
    }, [tree.projects.length, hasTargets, hasInherited, targetIds, inheritedIds]); // eslint-disable-line react-hooks/exhaustive-deps

    return {applyAccessEntries, fetchAccessForNodes};
};

export default useAccessFetch;
